import { Box, Grid, InputBase } from '@mui/material';
import { SearchIcon } from '@react-pdf-viewer/search';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { IWorkspace, getWorkspaceUsers, usersAdvanceFilterQuery } from 'wisestep-api';
import { WsButton, WsCheckboxList, WsListingLoader } from 'wisestep-base';

import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import { ICheckBoxListOptions } from '../../../features/jobs/assign/components/AssignedRecruiters';
import { StyledFooter } from '../../../features/jobs/assign/types';
import { warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { RoleTypes } from '../helper/ClientSettingsHelper';

interface IAddViewers {
  selectedViewers: IWorkspace[];
  onInput?: (data: FieldValues) => void;
  scopeType: string;
}

const AddViewers = ({ selectedViewers, onInput, scopeType }: IAddViewers) => {
  const methods = useForm();
  const { watch, setValue, reset, handleSubmit } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [viewersList, setViewersList] = useState<IWorkspace[]>([]);
  const [filteredList, setFilteredList] = useState<IWorkspace[]>([]);
  const selectViewers = watch('viewerList');
  const abortControllerRef = useRef(new AbortController());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const query = {
        roles: [
          RoleTypes.Analyst,
          RoleTypes.Head,
          RoleTypes.Lead,
          RoleTypes.User,
          RoleTypes.ACCOUNT_MANAGER,
        ],
        statuses: ['activated'],
        userIds: [],
        page: 0,
        size: 50,
      };
      const req = await getWorkspaceUsers(query);
      const selectedData: IWorkspace[] = req.data.workspaceUsers;
      const uniqueViewers = removeDuplicatesFromArray(selectedData, selectedViewers, 'userId');
      setViewersList(uniqueViewers);
      setFilteredList(uniqueViewers);
    } catch (error) {
      setViewersList([]);
      setFilteredList([]);
    } finally {
      setLoading(false);
    }
  }, [scopeType, selectedViewers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const removeDuplicatesFromArray = (sourceArray, filterArray, property) =>
    sourceArray.filter(
      (item) => !filterArray.some((filterItem) => filterItem[property] === item[property])
    );

  const viewerOptions: ICheckBoxListOptions = useMemo(
    () =>
      filteredList?.reduce((acc, item) => {
        if (item?.userId) {
          acc[item.userId] = item.userName || item.userId;
        }
        return acc;
      }, {} as ICheckBoxListOptions) || {},
    [filteredList]
  );

  const handleSelectEvents = (checked: boolean) =>
    setValue('viewerList', checked ? Object.keys(viewerOptions) : []);

  const save = (data) => {
    if (data?.viewerList?.length > 0) {
      onInput && onInput(data);
      reset();
    } else {
      setNotification(warning('Please select atleast one Viewer'));
    }
  };

  const onSearchInput = async ({ target: { value } }) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;
    if (!isEmpty(value)) {
      const response = await usersAdvanceFilterQuery(value);
      const uniqueViewers = removeDuplicatesFromArray(response.data, selectedViewers, 'userId');
      setFilteredList(uniqueViewers);
    } else {
      setFilteredList(viewersList);
    }
  };
  return (
    <Box>
      {isLoading ? (
        <WsListingLoader />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(save)}>
            <Grid container alignItems="center" pl={2} spacing={1}>
              <SearchIcon />
              <Grid item>
                <InputBase
                  placeholder="search"
                  name="searchtext"
                  onChange={onSearchInput}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid spacing={1}>
              <Grid container pl={2}>
                <WsCheckBox
                  name="selectAll"
                  label="Select All Viewers"
                  bold={true}
                  onClick={handleSelectEvents}
                />
              </Grid>
              <Grid container pl={2} pb={8.5}>
                <WsCheckboxList name="viewerList" options={viewerOptions} />
              </Grid>
            </Grid>
            <StyledFooter>
              <Grid container item justifyContent="space-between" py={1} pr={1}>
                <WsButton variant="text" size="large">
                  {`${selectViewers?.length ?? 0} Selected`}
                </WsButton>
                <WsButton size="large" variant="contained" type="submit">
                  Add
                </WsButton>
              </Grid>
            </StyledFooter>
          </form>
        </FormProvider>
      )}
    </Box>
  );
};

export default AddViewers;
